import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Button,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DoorFrontOutlinedIcon from "@mui/icons-material/DoorFrontOutlined";
import PriceChangeOutlinedIcon from "@mui/icons-material/PriceChangeOutlined";
import { PageHeader, PageShell } from "../../components/UI/Page";
import { EmptyState, LoadingState } from "../../components/UI/States";
import CheckUserAdmin from "../../components/User/CheckUserAdmin";
import SlabStylesManager from "../../components/Admin/SlabStylesManager";
import { selectUser } from "../../features/userSlice";

const LABOR_FIELDS = [
  { key: "processCost", label: "Process cost (single)" },
  { key: "processCostWithCasing", label: "Process cost w/ casing (single)" },
  { key: "processCostDouble", label: "Process cost (double)" },
  {
    key: "processCostDoubleWithCasing",
    label: "Process cost w/ casing (double)",
  },
];

const ADDER_FIELDS = [
  { key: "miscProduction", label: "Misc production" },
  { key: "screwPack", label: "Screw pack (single)" },
  { key: "screwPackDouble", label: "Screw pack (double)" },
  { key: "ftWaynePrep", label: "Ft. Wayne prep" },
];

const MULTIPLIER_FIELDS = [
  { key: "LF1x", label: "Per LF (single)" },
  { key: "LF2x", label: "Per LF (double)" },
  { key: "EA1x", label: "Each (single)" },
  { key: "EA2x", label: "Each (double)" },
];

const CATEGORY_LABELS = {
  casing: "Casing",
  hinge: "Hinges",
  jamb: "Jambs",
  stop: "Stops",
};

// Form state keeps everything as strings; parsing happens on save so a
// half-typed value never crashes an input.
const configToForm = (config) => ({
  labor: Object.fromEntries(
    LABOR_FIELDS.map(({ key }) => [key, String(config.labor?.[key] ?? "")])
  ),
  adders: Object.fromEntries(
    ADDER_FIELDS.map(({ key }) => [key, String(config.adders?.[key] ?? "")])
  ),
  defaultMultiplier: String(config.defaultMultiplier ?? ""),
  defaultMultiplierDouble: String(config.defaultMultiplierDouble ?? ""),
  multipliers: Object.fromEntries(
    Object.keys(CATEGORY_LABELS).map((category) => {
      const source = config.multipliers?.[category] || {};
      return [
        category,
        {
          ...Object.fromEntries(
            MULTIPLIER_FIELDS.map(({ key }) => [
              key,
              String(source[key] ?? ""),
            ])
          ),
          productCode: (source.productCode || []).join(", "),
        },
      ];
    })
  ),
});

const parseNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};

// Returns the payload for savePricingConfig, or a string describing the
// first invalid field.
const formToPayload = (form) => {
  const payload = {
    labor: {},
    adders: {},
    defaultMultiplier: parseNumber(form.defaultMultiplier),
    defaultMultiplierDouble: parseNumber(form.defaultMultiplierDouble),
    multipliers: {},
  };

  if (payload.defaultMultiplier === null) {
    return "Default multiplier (single) must be a non-negative number";
  }
  if (payload.defaultMultiplierDouble === null) {
    return "Default multiplier (double) must be a non-negative number";
  }

  for (const { key, label } of LABOR_FIELDS) {
    payload.labor[key] = parseNumber(form.labor[key]);
    if (payload.labor[key] === null) {
      return `${label} must be a non-negative number`;
    }
  }

  for (const { key, label } of ADDER_FIELDS) {
    payload.adders[key] = parseNumber(form.adders[key]);
    if (payload.adders[key] === null) {
      return `${label} must be a non-negative number`;
    }
  }

  for (const [category, categoryLabel] of Object.entries(CATEGORY_LABELS)) {
    const section = form.multipliers[category];
    const clean = {
      productCode: section.productCode.split(/[\s,]+/).filter(Boolean),
    };

    const badCode = clean.productCode.find((code) => !/^\d+$/.test(code));
    if (badCode) {
      return `${categoryLabel}: "${badCode}" is not a valid product code`;
    }

    for (const { key, label } of MULTIPLIER_FIELDS) {
      clean[key] = parseNumber(section[key]);
      if (clean[key] === null) {
        return `${categoryLabel} — ${label} must be a non-negative number`;
      }
    }

    payload.multipliers[category] = clean;
  }

  return payload;
};

// Shows a form value as money, tracking edits live; "—" until it parses.
const asMoney = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? `$${parsed.toFixed(2)}` : "—";
};

const FormulaTerm = ({ accent, title, subtitle, lines }) => (
  <div
    className={`flex-1 rounded-xl border p-3 ${accent.border} ${accent.bg}`}
  >
    <p className={`text-sm font-bold ${accent.text}`}>{title}</p>
    <p className="app-muted mb-2 text-xs italic">{subtitle}</p>
    <ul className="space-y-1 text-xs">
      {lines.map((line) => (
        <li key={line.label} className="flex justify-between gap-3">
          <span className="app-muted">{line.label}</span>
          <span className="whitespace-nowrap font-semibold tabular-nums">
            {line.value}
          </span>
        </li>
      ))}
    </ul>
  </div>
);

const FormulaOperator = ({ children }) => (
  <div className="app-muted flex items-center justify-center text-2xl font-bold lg:px-1">
    {children}
  </div>
);

// Mirrors the quotePricing math in server/routes/api/DoorKits/doorKit.js —
// if that formula changes, update this diagram with it.
const PricingFormulaDiagram = ({ form }) => (
  <div className="app-card mb-6 rounded-2xl border p-4 sm:p-5">
    <h2 className="mb-1 text-lg font-semibold">How a door is priced</h2>
    <p className="app-muted mb-4 text-sm">
      A door's cost is built from the three pieces below, using the numbers on
      this page — edits here update this preview as you type. The seller's
      margin then turns that cost into the quoted price.
    </p>

    <div className="flex flex-col gap-2 lg:flex-row lg:items-stretch lg:gap-3">
      <div className="app-surface flex items-center justify-center rounded-xl border px-4 py-2">
        <span className="whitespace-nowrap text-sm font-bold">
          Door Cost&nbsp;=
        </span>
      </div>

      <FormulaTerm
        accent={{
          border: "border-sky-500/30",
          bg: "bg-sky-500/5",
          text: "text-sky-600 dark:text-sky-300",
        }}
        title="Materials"
        subtitle="cost × multiplier, for each selected product"
        lines={[
          { label: "Cost", value: "override or ERP cost" },
          {
            label: "Multiplier",
            value: "category tables below",
          },
          {
            label: "Uncategorized product",
            value: `× ${form.defaultMultiplier || "—"} (dbl × ${
              form.defaultMultiplierDouble || "—"
            })`,
          },
        ]}
      />

      <FormulaOperator>+</FormulaOperator>

      <FormulaTerm
        accent={{
          border: "border-emerald-500/30",
          bg: "bg-emerald-500/5",
          text: "text-emerald-600 dark:text-emerald-300",
        }}
        title="Labor"
        subtitle="highest one that applies"
        lines={[
          { label: "Single door", value: asMoney(form.labor.processCost) },
          {
            label: "Single w/ casing",
            value: asMoney(form.labor.processCostWithCasing),
          },
          {
            label: "Double door",
            value: asMoney(form.labor.processCostDouble),
          },
          {
            label: "Double w/ casing",
            value: asMoney(form.labor.processCostDoubleWithCasing),
          },
          { label: "Slab only, no extras", value: "$0.00" },
        ]}
      />

      <FormulaOperator>+</FormulaOperator>

      <FormulaTerm
        accent={{
          border: "border-amber-500/30",
          bg: "bg-amber-500/5",
          text: "text-amber-600 dark:text-amber-300",
        }}
        title="Adders"
        subtitle="flat amounts on top"
        lines={[
          {
            label: "Misc production (every door)",
            value: asMoney(form.adders.miscProduction),
          },
          {
            label: "Screw pack (with hinges)",
            value: `${asMoney(form.adders.screwPack)} / dbl ${asMoney(
              form.adders.screwPackDouble
            )}`,
          },
          {
            label: "Ft. Wayne prep (when checked)",
            value: asMoney(form.adders.ftWaynePrep),
          },
        ]}
      />
    </div>

    <div className="mt-3 flex justify-center rounded-xl border border-violet-500/30 bg-violet-500/5 p-3">
      <p className="text-sm font-semibold">
        Quoted Price = Door Cost × Qty × 100 / (100 − margin%)
      </p>
    </div>

    <p className="app-muted mt-3 text-xs">
      Cost: when a product has an override on the Cost Overrides tab, that
      price is used; otherwise it's the product's average cost synced from the
      ERP.
    </p>
  </div>
);

const NumberField = ({ label, value, onChange }) => (
  <TextField
    size="small"
    label={label}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    inputProps={{ inputMode: "decimal" }}
    className="w-full"
  />
);

const OverrideRow = ({ product, userId, onSaved }) => {
  const [value, setValue] = useState(
    product.CostOverride != null ? String(product.CostOverride) : ""
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const save = async (clearing) => {
    setBusy(true);
    setError(null);
    try {
      const response = await fetch("/api/doors/kits/saveCostOverride", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-id": userId || "",
        },
        body: JSON.stringify({
          productCode: product.ProductCode,
          costOverride: clearing ? null : value,
        }),
      });
      const data = await response.json();
      if (!response.ok || data.error) {
        setError(data.message || "Save failed");
      } else {
        if (clearing) {
          setValue("");
        }
        onSaved(product.ProductCode, clearing ? null : Number(value));
      }
    } catch (err) {
      setError("Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr>
      <td className="font-medium tabular-nums">{product.ProductCode}</td>
      <td>{product.UOM}</td>
      <td className="tabular-nums">
        {product.AverageCostWithAdd != null
          ? `$${Number(product.AverageCostWithAdd).toFixed(2)}`
          : "—"}
      </td>
      <td>
        <TextField
          size="small"
          placeholder="No override"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          error={Boolean(error)}
          helperText={error}
          className="w-32"
          inputProps={{
            inputMode: "decimal",
            "aria-label": `Cost override for ${product.ProductCode}`,
          }}
        />
      </td>
      <td>
        <div className="flex justify-end gap-2">
          <Button
            size="small"
            variant="outlined"
            disabled={busy || value === ""}
            onClick={() => save(false)}
          >
            Save
          </Button>
          <Button
            size="small"
            color="error"
            disabled={busy || product.CostOverride == null}
            onClick={() => save(true)}
          >
            Clear
          </Button>
        </div>
      </td>
    </tr>
  );
};

const OverrideTable = ({ products, userId, onSaved }) => (
  <div className="app-card overflow-hidden rounded-2xl border">
    <div className="overflow-x-auto">
      <table className="app-table">
        <thead>
          <tr>
            {["Product Code", "UOM", "ERP Cost", "Override", ""].map(
              (header) => (
                <th key={header} className="uppercase tracking-wider">
                  {header}
                </th>
              )
            )}
          </tr>
        </thead>
        <tbody>
          {products.map((product) => (
            <OverrideRow
              key={product.ProductCode}
              product={product}
              userId={userId}
              onSaved={onSaved}
            />
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const PricingConfigTab = ({ userId }) => {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await fetch("/api/doors/kits/getPricingConfig");
        const data = await response.json();
        setForm(configToForm(data.config || {}));
      } catch (err) {
        setStatus({ ok: false, message: "Unable to load pricing settings." });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const setLabor = (key, value) =>
    setForm((prev) => ({ ...prev, labor: { ...prev.labor, [key]: value } }));

  const setAdder = (key, value) =>
    setForm((prev) => ({ ...prev, adders: { ...prev.adders, [key]: value } }));

  const setCategory = (category, key, value) =>
    setForm((prev) => ({
      ...prev,
      multipliers: {
        ...prev.multipliers,
        [category]: { ...prev.multipliers[category], [key]: value },
      },
    }));

  const handleSave = async () => {
    const payload = formToPayload(form);
    if (typeof payload === "string") {
      setStatus({ ok: false, message: payload });
      return;
    }

    setSaving(true);
    setStatus(null);
    try {
      const response = await fetch("/api/doors/kits/savePricingConfig", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-id": userId || "",
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok || data.error) {
        setStatus({ ok: false, message: data.message || "Save failed" });
      } else {
        setStatus({
          ok: true,
          message: "Saved — new quotes use these values immediately.",
        });
      }
    } catch (err) {
      setStatus({ ok: false, message: "Save failed" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingState label="Loading pricing settings…" />;
  }

  if (!form) {
    return (
      <p
        role="alert"
        className="rounded-xl bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300"
      >
        {status?.message || "Unable to load pricing settings."}
      </p>
    );
  }

  return (
    <>
      <PricingFormulaDiagram form={form} />

      <div className="app-card mb-6 rounded-2xl border p-4 sm:p-5">
        <h2 className="mb-4 text-lg font-semibold">Labor &amp; Adders</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {LABOR_FIELDS.map(({ key, label }) => (
            <NumberField
              key={key}
              label={label}
              value={form.labor[key]}
              onChange={(value) => setLabor(key, value)}
            />
          ))}
          {ADDER_FIELDS.map(({ key, label }) => (
            <NumberField
              key={key}
              label={label}
              value={form.adders[key]}
              onChange={(value) => setAdder(key, value)}
            />
          ))}
        </div>
      </div>

      <div className="app-card mb-6 rounded-2xl border p-4 sm:p-5">
        <h2 className="mb-1 text-lg font-semibold">Category Multipliers</h2>
        <p className="app-muted mb-4 text-sm">
          Products not listed in any category use the default multipliers.
        </p>
        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:w-1/2">
          <NumberField
            label="Default multiplier (single)"
            value={form.defaultMultiplier}
            onChange={(value) =>
              setForm((prev) => ({ ...prev, defaultMultiplier: value }))
            }
          />
          <NumberField
            label="Default multiplier (double)"
            value={form.defaultMultiplierDouble}
            onChange={(value) =>
              setForm((prev) => ({
                ...prev,
                defaultMultiplierDouble: value,
              }))
            }
          />
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {Object.entries(CATEGORY_LABELS).map(([category, label]) => (
            <div key={category} className="app-surface rounded-xl border p-4">
              <h3 className="mb-3 font-semibold">{label}</h3>
              <div className="mb-3 grid grid-cols-2 gap-3">
                {MULTIPLIER_FIELDS.map(({ key, label: fieldLabel }) => (
                  <NumberField
                    key={key}
                    label={fieldLabel}
                    value={form.multipliers[category][key]}
                    onChange={(value) => setCategory(category, key, value)}
                  />
                ))}
              </div>
              <TextField
                size="small"
                label="Product codes (comma separated)"
                value={form.multipliers[category].productCode}
                onChange={(e) =>
                  setCategory(category, "productCode", e.target.value)
                }
                multiline
                minRows={2}
                className="w-full"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="app-card flex flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl border px-4 py-3 sm:px-5">
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save Pricing Settings"}
        </Button>
        {status && (
          <p
            role="status"
            className={`text-sm ${
              status.ok
                ? "text-emerald-700 dark:text-emerald-300"
                : "text-rose-700 dark:text-rose-300"
            }`}
          >
            {status.message}
          </p>
        )}
      </div>
    </>
  );
};

const CostOverridesTab = ({ userId }) => {
  const [overrides, setOverrides] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searchError, setSearchError] = useState(null);
  const [searching, setSearching] = useState(false);

  const fetchOverrides = useCallback(async () => {
    try {
      const response = await fetch("/api/doors/kits/getCostOverrides");
      const data = await response.json();
      setOverrides(data.products || []);
    } catch (err) {
      // Non-fatal: the section just renders empty.
    }
  }, []);

  useEffect(() => {
    fetchOverrides();
  }, [fetchOverrides]);

  const handleSearch = async () => {
    const term = searchTerm.trim();
    if (!term) {
      setSearchResults(null);
      setSearchError(null);
      return;
    }

    setSearching(true);
    setSearchError(null);
    try {
      const response = await fetch(
        `/api/doors/kits/searchProductCosts?term=${encodeURIComponent(term)}`
      );
      const data = await response.json();
      if (!response.ok || data.error) {
        setSearchError(data.message || "Search failed");
        setSearchResults(null);
      } else {
        setSearchResults(data.products || []);
      }
    } catch (err) {
      setSearchError("Search failed");
      setSearchResults(null);
    } finally {
      setSearching(false);
    }
  };

  // Keep both the search results and the overrides list in sync after a
  // row-level save without refetching everything.
  const handleOverrideSaved = (productCode, override) => {
    const patch = (list) =>
      list.map((product) =>
        product.ProductCode === productCode
          ? { ...product, CostOverride: override }
          : product
      );

    if (searchResults) {
      setSearchResults(patch(searchResults));
    }
    setOverrides((prev) => {
      const patched = patch(prev);
      return override == null
        ? patched.filter((product) => product.ProductCode !== productCode)
        : patched;
    });
    fetchOverrides();
  };

  return (
    <>
      <p className="app-muted mb-4 text-sm">
        Overrides replace the synced ERP cost for a product and survive the
        nightly pricing sync. Clear an override to go back to the ERP cost.
      </p>

      <div className="mb-4 flex w-full gap-2 sm:w-[28rem]">
        <TextField
          size="small"
          placeholder="Search by product code…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          className="w-full"
          inputProps={{ "aria-label": "Search by product code" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Button variant="outlined" onClick={handleSearch} disabled={searching}>
          Search
        </Button>
      </div>

      {searchError && (
        <p
          role="alert"
          className="mb-4 rounded-xl bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300"
        >
          {searchError}
        </p>
      )}

      {searchResults &&
        (searchResults.length ? (
          <div className="mb-6">
            <OverrideTable
              products={searchResults}
              userId={userId}
              onSaved={handleOverrideSaved}
            />
          </div>
        ) : (
          <p className="app-surface app-muted mb-6 rounded-xl border px-4 py-3 text-sm">
            No products match that code.
          </p>
        ))}

      <h3 className="mb-3 text-lg font-semibold">Active Overrides</h3>
      {overrides.length ? (
        <OverrideTable
          products={overrides}
          userId={userId}
          onSaved={handleOverrideSaved}
        />
      ) : (
        <div className="app-card rounded-2xl border">
          <EmptyState
            compact
            icon={PriceChangeOutlinedIcon}
            title="No overrides are set."
            description="Search for a product code above to override its ERP cost."
          />
        </div>
      )}
    </>
  );
};

const DoorPricing = () => {
  const user = useSelector(selectUser);
  const [tab, setTab] = useState(0);

  return (
    <PageShell size="xl">
      <CheckUserAdmin />

      <PageHeader
        icon={DoorFrontOutlinedIcon}
        tone="slate"
        eyebrow="Admin"
        eyebrowTo="/admin"
        title="Interior Door Admin"
        description="Everything the interior door configurator sells and charges — labor, multipliers, slab styles, widths, and cost overrides."
      />

      <div className="mb-6 border-b app-divider">
        <Tabs
          value={tab}
          onChange={(e, value) => setTab(value)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Pricing" />
          <Tab label="Slab Styles & Widths" />
          <Tab label="Cost Overrides" />
        </Tabs>
      </div>

      {tab === 0 && <PricingConfigTab userId={user?.id} />}
      {tab === 1 && <SlabStylesManager userId={user?.id} />}
      {tab === 2 && <CostOverridesTab userId={user?.id} />}
    </PageShell>
  );
};

export default DoorPricing;
